import type { NotificationResponse } from '@orion/contracts';

/** Resolves a notification's `payload` (set by whichever module called
 * `NotificationsService#notify` — apps/api/src/modules/*) into a route to
 * navigate to. Not every notification type has a real destination screen
 * yet (`note.mention` would need the note's parent record, which isn't in
 * the payload; `task.reminder` has no task detail page) — those return
 * `null` rather than guessing, and the UI just doesn't make them
 * clickable. */
export function notificationLink(n: NotificationResponse): { to: string; params?: Record<string, string> } | null {
  const payload = n.payload;
  switch (n.type) {
    case 'lead.created':
    case 'lead.assigned':
    case 'pulse.lead_created':
      return typeof payload.leadId === 'string' ? { to: '/leads/$leadId', params: { leadId: payload.leadId } } : null;
    case 'pulse.reply_received':
    case 'calendly.meeting_booked':
    case 'calendly.meeting_canceled':
      if (typeof payload.leadId === 'string') return { to: '/leads/$leadId', params: { leadId: payload.leadId } };
      if (typeof payload.contactId === 'string') return { to: '/contacts/$contactId', params: { contactId: payload.contactId } };
      return null;
    case 'deal.won':
    case 'deal.lost':
      return typeof payload.opportunityId === 'string' ? { to: '/deals/$dealId', params: { dealId: payload.opportunityId } } : null;
    case 'import.completed':
      return { to: '/leads/import' };
    case 'webhook.disabled':
      return { to: '/admin/webhooks' };
    case 'pulse.reconnect_reminder':
      return { to: '/admin/integrations/inbox-pulse' };
    case 'ai_qualification.needs_review':
    case 'ai_qualification.failed':
      return { to: '/ai-review' };
    case 'whatsapp.lead_created':
      return typeof payload.leadId === 'string' ? { to: '/leads/$leadId', params: { leadId: payload.leadId } } : { to: '/whatsapp' };
    case 'whatsapp.message_received':
    case 'whatsapp.conversation_assigned':
      return { to: '/whatsapp' };
    default:
      return null;
  }
}
